<script setup lang="ts">
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { onMounted, computed, ref, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useMenuPageLayoutStore } from '@/stores/menuPageLayout'
import NavBar from '@/components/NavBar.vue'
import MainFooter from '@/components/MainFooter.vue'
import FloatingActions from '@/components/FloatingActions.vue'
import CookieBanner from '@/components/CookieBanner.vue'
import UpdateNotification from '@/components/UpdateNotification.vue'
import PageLoader from '@/components/PageLoader.vue'
import MobileBottomNav from '@/components/MobileBottomNav.vue'
import { settingsService } from '@/services/api'

const userStore = useUserStore()
const menuPageLayoutStore = useMenuPageLayoutStore()
const route = useRoute()

const isSettingsPage = computed(() => route.path === '/settings')
const isHomePage = computed(() => route.path === '/')
const isAdminPage = computed(() => route.path.startsWith('/admin'))
const isMenuRoute = computed(() => route.name === 'menu' || route.name === 'menu-page')
const hideSiteChrome = computed(() => isMenuRoute.value && menuPageLayoutStore.isStandalone)

// Under construction state
const underConstructionEnabled = ref(false)

// Site background color
const siteBackgroundColor = ref('#ffffff')

// Page loader state
const showPageLoader = ref(false)
const pageLoaderRef = ref<any>(null)

// Watch route changes to show loader on home page
watch(
  route,
  (newRoute) => {
    if (newRoute.name !== 'menu' && newRoute.name !== 'menu-page') {
      menuPageLayoutStore.reset()
    }

    if (newRoute.name === 'home') {
      showPageLoader.value = true
      // Start loader immediately when Vue is ready
      setTimeout(() => {
        if (pageLoaderRef.value && pageLoaderRef.value.startLoader) {
          pageLoaderRef.value.startLoader()
        }
      }, 50) // Minimal delay to ensure component mount
    } else {
      showPageLoader.value = false
    }
  },
  { immediate: true },
)

// Load site settings and update page title
const loadSiteSettings = async () => {
  try {
    const response = await settingsService.getSettings()
    if (response.success && response.settings) {
      // Check under construction status
      underConstructionEnabled.value = response.settings.under_construction_enabled === 'true'

      // Apply site background color
      if (response.settings.site_background_color) {
        siteBackgroundColor.value = response.settings.site_background_color
        applySiteBackgroundColor(response.settings.site_background_color)
      }

      // Update PWA theme color dynamically
      if (response.settings.pwa_theme_color) {
        updateThemeColor(response.settings.pwa_theme_color)
      }

      // Try company_name first, then fall back to site_title
      if (response.settings.company_name && response.settings.company_name.trim() !== '') {
        document.title = response.settings.company_name
      } else if (response.settings.site_title && response.settings.site_title.trim() !== '') {
        document.title = response.settings.site_title
      }
    }
  } catch (error) {
    console.error('Failed to load site settings:', error)
  }
}

// Update theme-color meta tag
const updateThemeColor = (color: string) => {
  let metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta')
    metaThemeColor.setAttribute('name', 'theme-color')
    document.head.appendChild(metaThemeColor)
  }
  metaThemeColor.setAttribute('content', color)
}

// Apply site background color to body and html
const applySiteBackgroundColor = (color: string) => {
  if (color) {
    document.body.style.setProperty('background-color', color, 'important')
    document.documentElement.style.setProperty('background-color', color, 'important')
  }
}

// Watch for siteBackgroundColor changes
watch(siteBackgroundColor, (newColor) => {
  if (newColor) {
    applySiteBackgroundColor(newColor)
  }
})

onMounted(() => {
  userStore.initializeAuth()
  loadSiteSettings()
  
  // Force GIF animations to play on mobile devices
  const forceGifAnimation = () => {
    const gifImages = document.querySelectorAll('img[src$=".gif"], img[src*=".gif?"]')
    gifImages.forEach((img: any) => {
      // Force redraw
      const src = img.src
      img.src = ''
      setTimeout(() => {
        img.src = src
      }, 10)
    })
  }
  
  // Run on mount
  setTimeout(forceGifAnimation, 500)
  
  // Run on any route change
  watch(route, () => {
    setTimeout(forceGifAnimation, 300)
  })
})
</script>

<template>
  <div id="app">
    <!-- Page Loader (shows on home page and covers everything) -->
    <PageLoader v-if="showPageLoader" ref="pageLoaderRef" :showProgress="true" />

    <!-- Navigation Bar (only for non-admin pages and when not under construction) -->
    <NavBar v-if="!isAdminPage && !underConstructionEnabled && !hideSiteChrome" />

    <!-- Main Content -->
    <div
      class="main-content"
      :class="{
        'admin-layout': isAdminPage,
        'with-navbar': !isAdminPage && !underConstructionEnabled && !hideSiteChrome,
        'under-construction': underConstructionEnabled,
      }"
    >
      <router-view />
    </div>

    <!-- Footer (only for non-admin pages and when not under construction) -->
    <MainFooter v-if="!isAdminPage && !underConstructionEnabled && !hideSiteChrome" />

    <!-- Floating Cart/Wishlist/ScrollTop Actions (hide when under construction) -->
    <FloatingActions v-if="!underConstructionEnabled && !hideSiteChrome" />

    <!-- GDPR Cookie Banner (hide when under construction and in admin pages) -->
    <CookieBanner v-if="!underConstructionEnabled && !isAdminPage && !hideSiteChrome" />

    <!-- Update Notification -->
    <UpdateNotification />

    <!-- Mobile Bottom Navigation (only on mobile, hide in admin and under construction) -->
    <MobileBottomNav v-if="!underConstructionEnabled && !isAdminPage && !hideSiteChrome" />
  </div>
</template>

<style>
html,
body {
  overflow-x: hidden;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

#app {
  min-height: 100vh;
  /* Removed problematic flex properties */
  /* display: flex; */
  /* flex-direction: column; */
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
  overflow-x: hidden;
  position: relative; /* Ensure proper stacking context for floating actions */
}

/* Default state - normal layout */
#app:not(.full-width) {
  max-width: none !important;
  margin: 0 auto !important;
  padding: 0 !important;
}

/* Full width for settings page */
#app.full-width {
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 100vw;
  overflow-x: hidden;
}

#app.full-width main {
  margin: 0 !important;
  padding: 0 !important;
  max-width: none !important;
}

/* Normal layout for home and other pages */
#app.home-page main {
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

main {
  /* Removed overflow-x: hidden — it breaks position:sticky on child elements */
  width: 100%;
  min-height: calc(100vh - 80px); /* Account for navbar height */
  /* Add padding-bottom to prevent floating actions from covering content */
  padding-bottom: 120px;
}

/* Admin pages shouldn't have extra padding for floating actions */
.admin-layout main {
  padding-bottom: 20px;
}

.router-link-active {
  font-weight: bold;
}

.navbar-brand {
  font-weight: bold;
  font-size: 1.5rem;
}

/* Ensure floating actions don't interfere with scroll */
.main-content {
  position: relative;
  z-index: 0;
}

/* Under construction layout */
.under-construction main {
  min-height: 100vh;
  padding-bottom: 0;
}

/* Mobile adjustments for better spacing */
@media (max-width: 768px) {
  main {
    padding-bottom: 100px; /* Less padding on mobile */
  }

  .admin-layout main {
    padding-bottom: 20px;
  }

  .under-construction main {
    padding-bottom: 0;
  }
}

/* CRITICAL: Override Bootstrap's img styles for floated images in article content */
/* Must be at the end to have highest specificity */
.article-body img[style*="float"] {
  display: inline !important;
  max-width: none !important;
}

.article-body img[style*="width"] {
  max-width: none !important;
}

/* Ensure GIF animations play on mobile devices */
img[src$=".gif"],
img[src*=".gif?"],
img[src*="gallery/"][src$=".gif"],
img[src*="gallery/"][src*=".gif?"] {
  image-rendering: auto !important;
  -webkit-transform: translateZ(0) !important;
  transform: translateZ(0) !important;
  will-change: auto !important;
  backface-visibility: hidden !important;
  -webkit-backface-visibility: hidden !important;
}

/* Remove all margins with 80px value when sidebar layout is active */
body.sidebar-layout-active [style*="margin-top: 80px"],
body.sidebar-layout-active [style*="margin-top:80px"],
body.sidebar-layout-active .page-hero,
body.sidebar-layout-active .page-header,
body.sidebar-layout-active .cart-header,
body.sidebar-layout-active .checkout-header,
body.sidebar-layout-active .wishlist-header,
body.sidebar-layout-active .breadcrumbs-section,
body.sidebar-layout-active .login-container,
body.sidebar-layout-active .hero-section,
body.sidebar-layout-active .gallery-section {
  margin-top: 0 !important;
}

</style>
